// utils/objectFlattener.ts

/**
 * Flattens a nested object into dot-notation key-value pairs
 * @param obj - Object to flatten
 * @param prefix - Optional prefix for keys
 * @param delimiter - Separator for nested keys (default: '.')
 * @returns Flattened object
 */
export function flattenObject(
  obj: Record<string, any>,
  prefix = "",
  delimiter = ".",
): Record<string, any> {
  const result: Record<string, any> = {};

  for (const key in obj) {
    if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;

    const value = obj[key];
    const newKey = prefix ? `${prefix}${delimiter}${key}` : key;

    // If value is a non-empty object (not array, not null), recurse
    if (
      value !== null &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      Object.keys(value).length > 0
    ) {
      Object.assign(result, flattenObject(value, newKey, delimiter));
    } else {
      result[newKey] = value;
    }
  }

  return result;
}

/**
 * Merges base metrics with flattened nested stats
 * @param baseMetrics - Flat object with base metrics
 * @param nestedStats - Nested object to flatten and merge
 * @returns Combined flat object
 */
export function mergeMetrics(
  baseMetrics: Record<string, any>,
  nestedStats: Record<string, any>,
): Record<string, any> {
  const flattenedStats = flattenObject(nestedStats);
  return {
    ...baseMetrics,
    ...flattenedStats,
  };
}
